import React, { useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useScrollTop } from 'Hooks/useScrollTop';
import { useEmailCoupon } from 'Hooks/useEmailCoupon';
import Header from 'Layouts/Header';
import { Footer } from 'Layouts/Footers';
import { DocHead } from 'Components/DocHead/DocHead';
import { ErrorBoundary } from 'Components/ErrorBoundary/ErrorBoundary';
import { identify, loadTealium } from 'Utils/Analytics';
import { heapIdentify } from 'Utils/Analytics/Heap';
import { setBrazeUser } from 'Utils/Braze';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Overlay from 'Layouts/Overlay';
import { loadPaypalSDK } from 'Api/PayPal';
import './AppLayout.css';
import { usePlans } from 'Hooks/usePlans';

const locationBasedClasses = {
  '/': 'landing',
  '/account/signup-confirmation': 'confirmed',
  '/signup-confirmation': 'confirmed',
  '/account/redeem': 'giftingHome',
  '/account/redeem/': 'giftingHome',
  '/account/review-and-redeem': 'giftingRedeem centerFrame',
};

const getPreSelectedPlanFromUrl = (pathname, routes) => {
  const routesArray = Object.values(routes);

  for (let i = 0; i < routesArray.length; i++) {
    const route = routesArray[i];

    if (route.includes('/:preSelectedPlan')) {
      const baseRoute = route.replace('/:preSelectedPlan', '');
      if (pathname.startsWith(baseRoute)) {
        const plan = pathname.replace(baseRoute, '').replaceAll('/', '');
        return plan;
      }
    }
  }
};

export const getDefaultClassNames = (pathname) => {
  return `defaultLayout ${locationBasedClasses[pathname] || 'centerFrame'}`;
};

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const { pathname = '' } = useLocation();
  const { tealiumProductEnv } = useSelector((s) => s.appConfig.defaultConfig);
  const { routes } = useSelector((s) => s.appConfig);
  const vaError = useSelector((s) => s.integrations.veteransAdv.error);

  /*Have to get from path because don't have access to react router useParams params
  here because this component is above the routes */
  const preSelectedPlan = useRef(getPreSelectedPlanFromUrl(pathname, routes));
  usePlans(preSelectedPlan.current);

  useEffect(() => {
    // call segment identify api
    identify();
    // load paypal SDK
    loadPaypalSDK();

    // call Heap identity
    // set Braze user
    const storedUserId = localStorage.getItem('ajs_user_id');
    const ajsUserId = storedUserId && JSON.parse(storedUserId);
    heapIdentify(ajsUserId);
    setBrazeUser(ajsUserId);

    // load tealium script
    loadTealium(tealiumProductEnv);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vaError) {
      navigate(routes.vetAdvantageError);
    }
  }, [navigate, routes.vetAdvantageError, vaError]);

  useScrollTop();
  useEmailCoupon();

  const showLayout = !pathname.startsWith(routes.invoice);

  const layoutClassName = useMemo(() => getDefaultClassNames(pathname), [pathname]);

  return (
    <ErrorBoundary name="app layout">
      <div className={`fn-app ${layoutClassName}`}>
        <div>
          <DocHead path={pathname} />
          {showLayout && (
            <div className="headerContainer sticky">
              <Header />
            </div>
          )}
          <div className="bodyContainer" role="main">
            {children}
          </div>
        </div>
        {showLayout && <Footer path={pathname} />}
      </div>
      <Overlay />
    </ErrorBoundary>
  );
}
